import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, Alert } from '@mui/material';

type ImportedUser = {
  email: string;
  first_name: string;
  last_name: string;
  role?: string;
  department?: string;
  location?: string;
  employment_status?: string;
  hire_date?: string;
  phone_number?: string;
  timezone?: string;
  employee_id?: string;
};

interface UsersImportDialogProps {
  open: boolean;
  onClose: () => void;
  onImport: (users: ImportedUser[]) => void;
}

const headerOrder = [
  'email',
  'first_name',
  'last_name',
  'role',
  'department',
  'location',
  'employment_status',
  'hire_date',
  'phone_number',
  'timezone',
  'employee_id',
];

const sampleTemplate = `${headerOrder.join(',')}\n` +
`admin@test.com,Admin,User,admin,Engineering,Headquarters,active,2025-01-01,555-555-5555,America/Los_Angeles,ADM-001\n` +
`manager@test.com,Manager,User,manager,Engineering,Headquarters,active,2025-01-02,555-555-1234,America/Los_Angeles,MGR-001\n` +
`employee@test.com,Employee,User,employee,Engineering,Temecula,active,2025-01-03,555-555-9999,America/Los_Angeles,EMP-001\n`;

const UsersImportDialog: React.FC<UsersImportDialogProps> = ({ open, onClose, onImport }) => {
  const [error, setError] = useState<string>('');
  const [parsed, setParsed] = useState<ImportedUser[]>([]);

  const handleFile = async (file: File) => {
    setError('');
    try {
      if (!file.name.toLowerCase().endsWith('.csv')) {
        setError('Please select a .csv file');
        return;
      }
      const text = await file.text();
      const lines = text.split(/\r?\n/).filter(l => l.trim().length > 0);
      if (lines.length < 2) {
        setError('CSV must include a header and at least one row');
        return;
      }
      const header = lines[0].split(',').map(h => h.trim());
      const missing = headerOrder.filter(h => !header.includes(h));
      if (missing.length) {
        setError(`Missing required columns: ${missing.join(', ')}`);
        return;
      }
      const items: ImportedUser[] = [];
      for (let i = 1; i < lines.length; i++) {
        const cols = lines[i].split(',');
        if (cols.length < header.length) continue;
        const row: any = {};
        header.forEach((h, idx) => {
          row[h] = (cols[idx] || '').trim();
        });
        // Basic validation
        if (!row.email || !row.first_name || !row.last_name) continue;
        items.push(row as ImportedUser);
      }
      if (!items.length) {
        setError('No valid rows found. Ensure required fields are provided.');
        return;
      }
      setParsed(items);
    } catch (e) {
      console.error(e);
      setError('Failed to parse CSV');
    }
  };

  const handleImport = () => {
    if (parsed.length > 0) {
      onImport(parsed);
      setParsed([]);
    }
  };

  const downloadTemplate = () => {
    const blob = new Blob([sampleTemplate], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'users_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Import Users from CSV</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Upload a CSV with the following columns: {headerOrder.join(', ')}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Department and Location values are matched by name (case-insensitive) to existing records.
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
          <Button variant="outlined" onClick={downloadTemplate}>Download Template</Button>
          <Button variant="contained" component="label">
            Choose CSV File
            <input type="file" accept=".csv" hidden onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleFile(f);
            }} />
          </Button>
          {parsed.length > 0 && (
            <Typography variant="body2">Loaded {parsed.length} users</Typography>
          )}
        </Box>
        <Typography variant="caption" color="text.secondary">
          Unknown departments/locations will be left empty; roles default to employee; employment_status defaults to active.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" disabled={parsed.length === 0} onClick={handleImport}>Import {parsed.length || ''}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default UsersImportDialog;

